// components/use-case-details/use-case-details-hero.tsx
"use client";

import { Clock } from "lucide-react";
import Image from "next/image";
import { useState } from "react";


interface UseCaseHeroProps {
  title: string;
  categories: string[];
  models: string[];
  tags: string[];
  readTime: string | null;
  publishDate: string | null;
  heroImage: string | null;
  overlayOpacity?: number;
}

export function UseCaseHero({
  title,
  categories,
  models,
  tags,
  readTime,
  publishDate,
  heroImage,
  overlayOpacity = 70
}: UseCaseHeroProps) {
  const [imageError, setImageError] = useState(false);
  
  // Updated checks to handle null heroImage explicitly
  const isValidImage = typeof heroImage === 'string' && heroImage.length > 0 && 
    (!heroImage.startsWith('http') || 
     heroImage.startsWith('/'));
     
  const useGradientFallback = !heroImage || !isValidImage || imageError;
  
  return (
    <div className="hero-section relative w-full h-[60vh] min-h-[500px] bg-gray-900">
      {/* Hero image with overlay */}
      <div className="absolute inset-0 z-0">
        {!useGradientFallback && heroImage ? (
          <Image
            src={heroImage}
            alt={title}
            fill
            className="object-cover opacity-60"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900" />
        )}
        <div className="absolute inset-0 bg-black bg-opacity-40" />
      </div>

      {/* Content */}
      <div className="container relative z-10 mx-auto px-4 h-full flex items-center">
        <div className="max-w-3xl">
          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-4">
            {categories?.map((category, index) => (
              <span
                key={index}
                className="bg-gray-800 bg-opacity-80 text-yellow-400 px-3 py-1 text-sm rounded-full"
              >
                {category}
              </span>
            ))}
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            {title}
          </h1>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {tags?.map((tag, index) => (
              <span
                key={index}
                className="bg-gray-700 bg-opacity-60 text-gray-300 px-2 py-1 text-xs rounded"
              >
                #{tag}
              </span>
            ))}
          </div>

          {/* Meta */}
          <div className="flex items-center text-gray-400 text-sm space-x-2">
            {readTime && (
              <span className="flex items-center">
                <Clock className="w-3.5 h-3.5 mr-1" /> 
                {readTime} read
              </span>
            )}
            {readTime && publishDate && <span className="opacity-50">•</span>}
            {publishDate && (
              <span>
                Published on {new Date(publishDate).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}